import React from 'react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import { PanelMarker } from '../../models/PanelMarker';
import { MathUtils } from '../../utils';

const blood_drop_current = require('../../assets/imgs/blood_drop_black.png');
const blood_drop_previous = require('../../assets/imgs/blood_drop_blue_50.png');

type Props = {
    panelMarker: PanelMarker;
    chartContainerClass: string;
    showPlotBandLabels: boolean;
};

type State = {
    panelMarker: PanelMarker;
    currentChartData: number;
    previousChartData: number;
}

// Use this flag to see actual percentFromMean values on chart
const DEBUG: boolean = false;
const DELAY: number = 2500;

export class ReferenceRangeChart extends React.Component<Props, State> {
    state: State = {
        panelMarker: this.props.panelMarker,
        currentChartData: undefined,
        previousChartData: undefined
    };

    componentWillReceiveProps(nextProps) {
        const { panelMarker  } = nextProps;
        this.setState({
            panelMarker,
            currentChartData: this.createChartData(panelMarker),
            previousChartData: this.createChartData(panelMarker.previousPanelMarker)
        });
    }

    componentDidMount() {
        this.populateChartData();
    }

    createChartData(panelMarker: PanelMarker) {
        if (!panelMarker || panelMarker.isBlank()) {
            return -1000; // This is a hack to not show the previous blood drop
        }

        let chartData: number;

        if (panelMarker.field.noRedChart) {
            chartData = panelMarker.altChartValueNoRed();
        } else if (panelMarker.field.noRedOrangeChart) {
            chartData = panelMarker.altChartValueNoRedOrange();
        } else {
            chartData = panelMarker.percentFromMean;
        }

        chartData = MathUtils.round(chartData, panelMarker.field.precision);

        if (chartData <= this.obtainChartMin(panelMarker)) {
            chartData = this.obtainChartMin(panelMarker);
        } else if (chartData >= this.obtainChartMax(panelMarker)) {
            chartData = this.obtainChartMax(panelMarker);
        }
        
        return chartData;
    }

    populateChartData() {
        const { panelMarker } = this.state;
        setTimeout(()=>{
            this.setState({
                currentChartData: this.createChartData(panelMarker),
                previousChartData: this.createChartData(panelMarker.previousPanelMarker)
            });
        }, DELAY);
    }

    obtainChartMin(panelMarker: PanelMarker): number {
        const pm = panelMarker;
        if (pm.field.noRedChart || pm.field.noRedOrangeChart) {
            return 0;
        }
        return -133.3;
    }

    obtainChartMax(panelMarker: PanelMarker): number {
        const pm = panelMarker;
        if (pm.field.noRedChart || pm.field.noRedOrangeChart) {
            return 266.67;
        }
        return 133.3;
    }

    obtainTickPositions(): Array<number> {
        const { panelMarker } = this.state;
        const pm = panelMarker;
        if (pm.field.noRedChart) {
            return [0, 66, 100, 133.33, 166.67, 200, 233.33, 266.67];
        }
        else if (pm.field.noRedOrangeChart) {
            return [0, 100, 133.33, 166.67, 200, 233.33, 266.67];
        }
        return [-133.3, -100, -66.6, -33.3, 0, 33.3, 66.6, 100, 133.3];
    }

    obtainPlotBands(): Array<Object> {
        const { panelMarker } = this.state;
        const { showPlotBandLabels } = this.props;
        const pm = panelMarker;
        const GREEN = '#78F880';
        const YELLOW = '#FEFA83';
        const ORANGE = '#FFD37E';
        const RED = '#FC6552';

        if (pm.field.noRedChart) {
            return [{
                label: {
                    // text: '< Lab',
                    align: 'center',
                    y: -5
                },
                from: 0,
                to: 66,
                color: ORANGE
            },{
                label: {
                    // text: 'Lab',
                    align: 'center',
                    y: -5
                },
                from: 66,
                to: 100,
                color: YELLOW
            },
            {
                label: {
                    // text: 'Optimal',
                    align: 'center',
                    y: -5
                },
                color: GREEN,
                from: 100,
                to: 166.67
            },
            {
                label: {
                    // text: 'Lab',
                    align: 'center',
                    y: -5
                },
                color: YELLOW,
                from: 166.67,
                to: 200
            },
            {
                label: {
                    // text: '> Lab',
                    align: 'center',
                    y: -5
                },
                from: 200,
                to: 233.33,
                color: ORANGE
            },
            {
                label: {
                    // text: 'Alarm',
                    align: 'center',
                   y: -5
                },
                from: 233.33,
                to: 266.67,
                color: RED
            }
            ];
        }
        else if (pm.field.noRedOrangeChart) {
            return [{
                label: {
                    // text: 'Lab',
                    align: 'center',
                    y: -5
                },
                from: 0,
                to: 100,
                color: YELLOW
            },
            {
                label: {
                    // text: 'Optimal',
                    align: 'center',
                    y: -5
                },
                color: GREEN,
                from: 100,
                to: 166.67
            },
            {
                label: {
                    // text: 'Lab',
                    align: 'center',
                    y: -5
                },
                color: YELLOW,
                from: 166.67,
                to: 200
            },
            {
                label: {
                    // text: '> Lab',
                    align: 'center',
                    y: -5
                },
                from: 200,
                to: 233.33,
                color: ORANGE
            },
            {
                label: {
                    // text: 'Alarm',
                    align: 'center',
                   y: -5
                },
                from: 233.33,
                to: 266.67,
                color: RED
            }
            ];
        }

        return [{
            label: {
                text: showPlotBandLabels ? 'Alarm' : '',
                align: 'center',
                y: -5
            },
            from: -133.3,
            to: -100,
            color: pm.field.noRedOrangeChart ? YELLOW : RED
        },
        {
            label: {
                text: showPlotBandLabels ? '< Lab' : '',
                align: 'center',
                y: -5
            },
            from: -100,
            to: -66.6,
            color: pm.field.noRedOrangeChart ? YELLOW : ORANGE
        },
        {
            label: {
                text: showPlotBandLabels ? 'Lab' : '',
                align: 'center',
                y: -5
            },
            from: -66.6,
            to: -33.3,
            color: YELLOW
        },
        {
            label: {
                text: showPlotBandLabels ? 'Optimal' : '',
                align: 'center',
                y: -5
            },
            from: -33.3,
            to: 33.3,
            color: GREEN
        },
        {
            label: {
                text: showPlotBandLabels ? 'Lab' : '',
                align: 'center',
                y: -5
            },
            color: YELLOW,
            from: 33.3,
            to: 66.6
        },
        {
            label: {
                text: showPlotBandLabels ? '> Lab' : '',
                align: 'center',
                y: -5
            },
            from: 66.6,
            to: 100,
            color: ORANGE
        },
        {
            label: {
                text: showPlotBandLabels ? 'Alarm' : '',
                align: 'center',
                y: -5
            },
            from: 100,
            to: 133.3,
            color: RED
        }
        ];
    }

    determineOffset(chartData: number): number {
        let offset = 0;
        if (chartData >= 0) {
            offset = -12;
        } else {
            offset = 12;
        }
        return offset;
    }

    render() {
        console.log('rendering ReferenceRangeChart ****************************************');
        const { chartContainerClass } = this.props;
        const { panelMarker } = this.state;
        const pm = panelMarker;

        // Render when the current OR the previous test has a value. A
        // "previous-only" comparison marker is blank in the current test but
        // carries a previousPanelMarker with a value — createChartData() sends
        // the blank current drop off-chart (-1000), leaving only the previous
        // (blue) drop visible.
        if (!pm.hasValue() && !(pm.previousPanelMarker && pm.previousPanelMarker.hasValue())) {
            return null;
        }

        const GRID_LINE_COLOR = '#CDD7EA';
        const BAR_COLOR = 'rgba(255, 255, 255, 0)';

        let currentChartData: number = this.state.currentChartData;
        let previousChartData: number = this.state.previousChartData;

        // For a "previous-only" comparison marker (no current value, previous
        // present) give the CURRENT series the SAME in-range point as the
        // previous drop and hide its (black) label, rather than leaving it
        // off-chart. An off-chart point in one series makes Highcharts shift the
        // other series' drop DOWNWARD off the bar; matching both series' points
        // keeps the blue drop centered exactly like every normal two-drop row.
        const isPreviousOnly = !pm.hasValue() && !!pm.previousPanelMarker && pm.previousPanelMarker.hasValue();
        const currentSeriesData = isPreviousOnly ? previousChartData : currentChartData;

        let offset = this.determineOffset(currentSeriesData);
        let offsetPrevious = this.determineOffset(previousChartData);

        const options: Highcharts.Options = {
            chart: {
                type: 'bar',
                marginTop: 20,
                marginBottom: 20,
            },
            // Overlay CURRENT and PREVIOUS series on the same row so the
            // previous blood drop renders at the same vertical position as
            // the current one. Without this, Highcharts splits the category
            // into two sub-rows and the previous drop sits below the current.
            plotOptions: {
                bar: { grouping: false, pointPlacement: 0 },
            },
            title: {
                text: ''
            },
            xAxis: [{
                labels: {
                    enabled: false
                },
                tickLength: 0
            }],
            yAxis: {
                title: {
                    text: ''
                },
                labels: {
                    enabled: true,
                    formatter: function () {
                        const self: any = this;
                        const digits = pm.field.precision;
                        if (DEBUG) {
                            return self.value;
                        }
                        if (pm.field.noRedChart) {
                            switch (self.value) {
                                case 0:
                                    return `${MathUtils.round(pm.range.alarmMin, digits)}`;
                                case 66:
                                    return `${MathUtils.round(pm.range.labMin, digits)}`;
                                case 100:
                                    return `${MathUtils.round(pm.range.optimalMin, digits)}`; 
                                case 166.67:
                                    return `${MathUtils.round(pm.range.optimalMax, digits)}`;  
                                case 200:
                                    return `${MathUtils.round(pm.range.labMax, digits)}`;  
                                case 233.33:
                                    return `${MathUtils.round(pm.range.alarmMax, digits)}`;          
                                default:
                                    return '';
                            }
                        } else if (pm.field.noRedOrangeChart) {
                            switch (self.value) {
                                case 0:
                                    return `${MathUtils.round(pm.range.labMin, digits)}`;
                                case 100:
                                    return `${MathUtils.round(pm.range.optimalMin, digits)}`; 
                                case 166.67:
                                    return `${MathUtils.round(pm.range.optimalMax, digits)}`;  
                                case 200:
                                    return `${MathUtils.round(pm.range.labMax, digits)}`;  
                                case 233.33:
                                    return `${MathUtils.round(pm.range.alarmMax, digits)}`;          
                                default:
                                    return '';
                            }
                        }
                        // default
                        switch (self.value) {
                            case -133.3: return '';
                            case -100:
                                return MathUtils.round(pm.range?.alarmMin, digits);
                            case -66.6:
                                return MathUtils.round(pm.range?.labMin, digits);
                            case -33.3:
                                return `${MathUtils.round(pm.range?.optimalMin, digits)}`;
                            case 0:
                                return `${MathUtils.round(pm.range?.mean, digits)}`;
                            case 33.3:
                                return `${MathUtils.round(pm.range?.optimalMax, digits)}`;
                            case 66.6:
                                return `${MathUtils.round(pm.range?.labMax, digits)}`;
                            case 100:
                                return `${MathUtils.round(pm.range?.alarmMax, digits)}`;
                            case 133.3:
                                return '';
                            default:
                                return self.value;
                        }
                    }
                },
                min: this.obtainChartMin(pm),
                max: this.obtainChartMax(pm),
                tickPositions: this.obtainTickPositions(),
                gridLineWidth: 1, // removes grid lines between bands if set to 0
                gridLineColor: GRID_LINE_COLOR,
                plotBands: this.obtainPlotBands()
            },
            tooltip: {
                followPointer: true,
                followTouchMove: true,
                padding: 5,
                useHTML: true,
                shared: true,
                formatter: function () {
                    var currentValue = pm && pm.hasValue() ? pm.displayValue() : 'N/A';
                    var previousValue = pm && pm.previousPanelMarker && pm.previousPanelMarker.hasValue() ? pm.previousPanelMarker.displayValue() : 'N/A';
                    return  `<b> Current: ${currentValue}<br>Previous: ${previousValue}</b>`;              
                }
            },
            series: [{
                name: 'CURRENT',
                type: 'bar',
                color: BAR_COLOR,
                borderWidth: 0,
                pointWidth: 10,
                data: [currentSeriesData],
                dataLabels: {
                    enabled: !isPreviousOnly,
                    allowOverlap: true,
                    useHTML: true,
                    verticalAlign: 'middle',
                    x: offset,
                    format: `<img src="${blood_drop_current}" />`
                }
            },
            {
                name: 'PREVIOUS',
                type: 'bar',
                color: BAR_COLOR,
                borderWidth: 0,
                pointWidth: 10,
                data: [previousChartData],
                dataLabels: {
                    enabled: true,
                    allowOverlap: true,
                    useHTML: true,
                    verticalAlign: 'middle',
                    x: offsetPrevious,
                    format: `<img src="${blood_drop_previous}" />`
                }
            }],
            legend: {
                enabled: false
            },
            credits: {
                enabled: false
            },
            exporting: {
                enabled: false
            }
        };

        return (
            <HighchartsReact 
                options={options}
                highcharts={Highcharts}
                containerProps = {{ className: chartContainerClass }}
            />
        );
    }
}